import { Search, X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@heidi/utils';

import { Button } from '../button';
import { useDesignSystemIntl } from '../hooks/use-design-system-intl';
import { Input } from '../input';
import { searchInputMessages } from './search-input.messages';
import {
  clearButtonClasses,
  searchIconClasses,
  suppressNativeSearchCancelClasses,
} from './search-input.styles';
import type { SearchInputProps } from './search-input.types';

const dispatchNativeInputChange = (node: HTMLInputElement, nextValue: string) => {
  // Bypass React's `_valueTracker` diff by driving the DOM setter directly,
  // then dispatch a bubbling `input` event so React re-runs its synthetic
  // change pipeline. This is the standard react-hook-form-compatible way to
  // programmatically clear a controlled input.
  // eslint-disable-next-line @typescript-eslint/unbound-method -- calling via .call() binds `this` explicitly
  const setter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    'value'
  )?.set;
  setter?.call(node, nextValue);
  node.dispatchEvent(new Event('input', { bubbles: true }));
};

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      accessoryEnd,
      className,
      onChange,
      onClear,
      clearAriaLabel,
      disabled,
      density = 'comfortable',
      value,
      defaultValue,
      ...props
    },
    ref
  ) => {
    const intl = useDesignSystemIntl();
    const innerRef = React.useRef<HTMLInputElement | null>(null);
    const setRefs = React.useCallback(
      (node: HTMLInputElement | null) => {
        innerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );

    const isControlled = value !== undefined;
    const [uncontrolledHasValue, setUncontrolledHasValue] = React.useState(
      () => String(defaultValue ?? '').length > 0
    );

    // Sync from the live DOM value for consumers that populate the initial
    // value imperatively via the ref (e.g. react-hook-form's `register` with
    // `defaultValues`) — those consumers never pass `defaultValue` as a
    // prop, so the useState seed above would leave the clear button hidden
    // until the first keystroke.
    //
    // Runs on mount and whenever `isControlled` flips (controlled→uncontrolled
    // reseeds from the DOM; the `if` guard makes the reverse a no-op). It
    // does NOT observe subsequent imperative DOM changes at runtime — e.g.
    // `form.reset()` bypasses onChange, so visibility won't update until the
    // user types. Consumers that need dynamic sync should use controlled mode.
    React.useLayoutEffect(() => {
      if (isControlled) return;
      const node = innerRef.current;
      if (node && node.value.length > 0) setUncontrolledHasValue(true);
    }, [isControlled]);

    const hasValue = isControlled ? String(value ?? '').length > 0 : uncontrolledHasValue;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) setUncontrolledHasValue(event.target.value.length > 0);
      onChange?.(event);
    };

    const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
      const node = innerRef.current;
      if (!node) return;
      dispatchNativeInputChange(node, '');
      node.focus();
      onClear?.(event);
    };

    const showClearButton = hasValue && !disabled;

    return (
      <Input
        ref={setRefs}
        type="search"
        density={density}
        disabled={disabled}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        // Merge the WebKit-cancel suppression with any consumer className so a
        // caller-supplied class doesn't wipe the suppression via prop spread.
        className={cn(suppressNativeSearchCancelClasses, className)}
        accessoryStart={
          <Search
            className={searchIconClasses(density, hasValue && !disabled)}
            aria-hidden="true"
          />
        }
        accessoryEnd={
          showClearButton || accessoryEnd ? (
            <>
              {showClearButton && (
                <Button
                  type="button"
                  variant="ghost"
                  size="xxs"
                  iconOnly
                  flush="end"
                  aria-label={
                    clearAriaLabel ?? intl.formatMessage(searchInputMessages.clearSearch)
                  }
                  onClick={handleClear}
                  className={clearButtonClasses}
                >
                  <X aria-hidden="true" />
                </Button>
              )}
              {accessoryEnd}
            </>
          ) : undefined
        }
        {...props}
      />
    );
  }
);
SearchInput.displayName = 'SearchInput';

export { SearchInput };
